import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { oesApi, adminApi, assessmentApi, settingsApi } from '../services/api';
import { getCriticalityLabels, getCriticalityRowColors } from '../utils/settingsScales';
import OESEditSidebar from '../components/admin/OESEditSidebar';
import BulkImportOESModal from '../components/admin/BulkImportOESModal';
import ConfirmModal from '../components/ConfirmModal';
import Layout from '../components/layout/Layout';
import CustomSelect from '../components/common/CustomSelect';
import { getCountryFlag } from '../utils/countryFlag';
import { useLabels } from '../providers/LabelsProvider';
import { useT } from '../providers/I18nProvider';

const SECTOR_LABELS = {
  'energy': 'Energy',
  'telecom': 'Telecommunications',
  'finance': 'Finance',
  'health': 'Healthcare',
  'transport': 'Transportation',
  'water': 'Water',
  'government': 'Government',
  'other': 'Other',
};

export default function OESManagement() {
  const labels = useLabels();
  const t = useT();
  const navigate = useNavigate();
  const location = useLocation();
  const [oesList, setOesList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [bulkImportOpen, setBulkImportOpen] = useState(false);
  const [selectedOES, setSelectedOES] = useState(null);
  const [assessments, setAssessments] = useState([]);
  const [selectedAssessmentId, setSelectedAssessmentId] = useState('');
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [oesToDelete, setOesToDelete] = useState(null);
  const [scales, setScales] = useState(null);
  const [sectorLists, setSectorLists] = useState([]);

  useEffect(() => {
    settingsApi.getForCurrentUser()
      .then((r) => setScales(r.data?.scales ?? {}))
      .catch(() => setScales({}));
    // Load sector lists for display
    adminApi.getSectorLists()
      .then((r) => setSectorLists(r.data || []))
      .catch(() => setSectorLists([]));
  }, []);

  const criticalityLabels = getCriticalityLabels(scales);
  const criticalityRowColors = getCriticalityRowColors(scales);

  // Helper function to get sector name from IDs
  const getSectorName = (sectorListId, sectorId) => {
    if (!sectorListId || !sectorId) return 'Unassigned';
    const list = sectorLists.find(sl => sl.id === sectorListId);
    if (!list || !list.sectors) return 'Unknown';
    const sector = list.sectors.find(s => s.id === sectorId);
    return sector ? sector.name : 'Unknown';
  };

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const assessmentIdParam = searchParams.get('assessment_id');
    if (assessmentIdParam) {
      setSelectedAssessmentId(assessmentIdParam);
    }
    fetchAssessments();
  }, [location.search]);

  useEffect(() => {
    fetchOES();
  }, [selectedAssessmentId]);

  const fetchAssessments = async () => {
    try {
      const response = await assessmentApi.getAll();
      const list = response.data || [];
      setAssessments(list);
      if (!selectedAssessmentId && list.length === 1) {
        setSelectedAssessmentId(String(list[0].id));
      }
    } catch (err) {
      console.error('Failed to load assessments', err);
    }
  };

  const fetchOES = async () => {
    try {
      setLoading(true);
      const assessmentId = selectedAssessmentId ? parseInt(selectedAssessmentId, 10) : null;
      const response = await oesApi.getAll(0, 100, null, null, assessmentId);
      setOesList(response.data);
    } catch (err) {
      setError(`Failed to load ${labels.entityName}`);
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateOES = () => {
    setSelectedOES(null);
    setSidebarOpen(true);
  };

  const handleEditOES = (oes) => {
    setSelectedOES(oes);
    setSidebarOpen(true);
  };

  const handleDeleteOES = (oesId) => {
    const oes = oesList.find(item => item.id === oesId);
    setOesToDelete(oes || null);
    setConfirmOpen(true);
  };

  const confirmDeleteOES = async () => {
    if (!oesToDelete) return;
    try {
      await adminApi.deleteOES(oesToDelete.id);
      fetchOES();
    } catch (err) {
      setError(`Failed to delete ${labels.entityName}`);
    } finally {
      setConfirmOpen(false);
      setOesToDelete(null);
    }
  };


  return (
    <Layout>
      <div className="max-w-7xl mx-auto px-4 py-8">
        {/* Page Title */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">
                {labels.entityName} Management
              </h1>
              <p className="text-gray-600 mt-1">
                {labels.entityNamePluralFull} - Critical Infrastructure Assessment
              </p>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={handleCreateOES}
                className="inline-flex items-center gap-1.5 px-3.5 py-2 text-xs font-medium bg-eucnet-navy text-white rounded-lg hover:bg-eucnet-blue transition-colors"
              >
                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                </svg>
                Create {labels.entityName}
              </button>
              <button
                onClick={() => setBulkImportOpen(true)}
                className="inline-flex items-center gap-1.5 px-3.5 py-2 text-xs font-medium border border-eucnet-navy text-eucnet-navy rounded-lg hover:bg-eucnet-navy hover:text-white transition-colors"
              >
                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                </svg>
                {t('entity.bulkImport')}
              </button>
            </div>
          </div>
        </div>

        {/* Assessment Filter */}
        <div className="mb-6 bg-white rounded-lg shadow p-4">
          <div className="flex items-center gap-4">
            <label className="text-sm font-medium text-slate-700">{t('common.assessment')}:</label>
            <div className="w-64">
              <CustomSelect
                value={selectedAssessmentId}
                onChange={(e) => setSelectedAssessmentId(e.target.value)}
                options={[
                  { value: '', label: t('admin.allAssessments') },
                  ...assessments.map(a => ({ value: String(a.id), label: a.name }))
                ]}
                placeholder={t('admin.allAssessments')}
              />
            </div>
          </div>
        </div>

        {/* Error Message */}
        {error && (
          <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700">
            {error}
          </div>
        )}

        {/* Info Panel */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
          <h3 className="font-semibold text-blue-900 mb-2">{`📊 About ${labels.entityNamePluralFull} (${labels.entityName})`}</h3>
          <p className="text-blue-800 text-sm">
            {`${labels.entityNamePluralFull} (${labels.entityName}) are public or private entities in critical sectors whose services are essential for maintaining critical societal or economic activities.`}
          </p>
        </div>

        {/* OES List */}
        {loading ? (
          <div className="text-center py-12">
            <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
            <p className="text-gray-600 mt-4">{`Loading ${labels.entityName}...`}</p>
          </div>
        ) : oesList.length === 0 ? (
          <div className="bg-white rounded-lg shadow p-12 text-center">
            <img 
              src={`${process.env.PUBLIC_URL}/oes_icon.png`} 
              alt="OES Icon" 
              className="mx-auto h-16 w-16 mb-4"
            />
            <h3 className="text-xl font-semibold text-gray-900 mb-2">{`No ${labels.entityName} Found`}</h3>
            <p className="text-gray-600 mb-4">
              {`Create your first ${labels.entityNameFull} to begin assessment`}
            </p>
            <button
              onClick={handleCreateOES}
              className="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-medium bg-eucnet-navy text-white rounded-lg hover:bg-eucnet-blue transition-colors"
            >
              {`Create First ${labels.entityName}`}
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {oesList.map((oes) => (
              <div key={oes.id} className="bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow flex flex-col relative">
                {/* Edit/Delete Icons - Top Right Corner */}
                <div className="absolute top-4 right-4 flex items-center gap-2 z-10">
                  <button
                    onClick={() => handleEditOES(oes)}
                    className="text-eucnet-blue hover:text-eucnet-navy transition-colors"
                    title={`Edit ${labels.entityName}`}
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                    </svg>
                  </button>
                  <button
                    onClick={() => handleDeleteOES(oes.id)}
                    className="text-red-600 hover:text-red-700 pb-2 transition-colors"
                    title={`Delete ${labels.entityName}`}
                  >
                    <svg className="w-8 h-8 fill-current" viewBox="0 -3 32 32">
                      <path d="M13 15h2v6h-2zM17 15h2v6h-2z" />
                      <path d="M20 9c0-.6-.4-1-1-1h-6c-.6 0-1 .4-1 1v2H8v2h1v10c0 .6.4 1 1 1h12c.6 0 1-.4 1-1V13h1v-2h-4V9zm-6 1h4v1h-4v-1zm7 3v9H11v-9h10z" />
                    </svg>
                  </button>
                </div>

                {/* Header with Logo, Name */}
                <div className="mb-4 pr-20">
                  {oes.logo && (
                    <div className="mb-3 flex justify-center">
                      <img
                        src={oes.logo.startsWith('data:') ? oes.logo : `data:image/jpeg;base64,${oes.logo}`}
                        alt={`${oes.name} logo`}
                        className="h-12 w-auto max-w-full object-contain"
                      />
                    </div>
                  )}
                  
                  <div className="flex-1 min-w-0">
                    <h3 className="text-lg font-semibold text-eucnet-navy line-clamp-2">
                      {oes.name}
                    </h3>
                    {oes.short_name && (
                      <p className="text-sm text-eucnet-grey-600 mt-1">{oes.short_name}</p>
                    )}
                  </div>
                  
                  {/* Sector Badge */}
                  <span className="inline-block px-2 py-1 bg-blue-100 text-blue-800 text-xs font-semibold rounded mt-2">
                    {getSectorName(oes.sector_list_id, oes.sector_id)}
                  </span>
                </div>

                {/* Description - Fixed height with truncation */}
                <div className="mb-4 min-h-[2.5rem]">
                  {oes.description ? (
                    <p className="text-sm text-eucnet-grey-600 line-clamp-2">
                      {oes.description}
                    </p>
                  ) : (
                    <p className="text-sm text-eucnet-grey-400 italic">{t('common.noDescriptionProvided')}</p>
                  )}
                </div>

                {/* Contact Information - Only show if at least one field exists */}
                {(oes.country || oes.email || oes.website) && (
                  <div className="space-y-2 text-sm mb-4">
                    {oes.country && (
                      <div className="flex items-center text-eucnet-grey-600">
                        <svg className="w-4 h-4 mr-2 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        <span className="truncate">{oes.country}</span>
                        <span className="text-2xl ml-1 inline-block align-middle" aria-hidden="true">{getCountryFlag(oes.country)}</span>
                      </div>
                    )}
                    {oes.email && (
                      <div className="flex items-center text-eucnet-grey-600">
                        <svg className="w-4 h-4 mr-2 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                        </svg>
                        <a 
                          href={`mailto:${oes.email}`}
                          className="hover:text-eucnet-blue truncate"
                          title={`Send email to ${oes.email}`}
                        >
                          {oes.email}
                        </a>
                      </div>
                    )}
                    {oes.website && (
                      <div className="flex items-center text-eucnet-grey-600">
                        <svg className="w-4 h-4 mr-2 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9a9 9 0 01-9-9m9 9c1.657 0 3-4.03 3-9s-1.343-9-3-9m0 18c-1.657 0-3-4.03-3-9s1.343-9 3-9m-9 9a9 9 0 019-9" />
                        </svg>
                        <a 
                          href={oes.website.startsWith('http') ? oes.website : `https://${oes.website}`}
                          target="_blank" 
                          rel="noopener noreferrer" 
                          className="hover:text-eucnet-blue truncate"
                          title={`Visit ${oes.website}`}
                        >
                          {oes.website}
                        </a>
                      </div>
                    )}
                  </div>
                )}

                {/* Criticality Status - Fixed height section */}
                <div className="mt-auto">
                  <div className="mt-4 p-3 bg-gray-50 rounded min-h-[5rem] flex flex-col justify-center">
                    {oes.oes_criticality ? (
                      <div>
                        <div className="text-xs text-gray-500 mb-1">{t('common.currentCriticalityLevel')}</div>
                        <div className="flex items-center justify-between">
                          <span className={`inline-block px-3 py-1.5 rounded-lg font-semibold text-sm ${criticalityRowColors[oes.oes_criticality.toLowerCase()] || 'bg-gray-200 text-gray-800'}`}>
                            {criticalityLabels[oes.oes_criticality.toLowerCase()] || oes.oes_criticality}
                          </span>
                        </div>
                      </div>
                    ) : (
                      <div className="text-center">
                        <div className="text-gray-500 text-sm mb-2">
                          ⚠️ No assessment completed
                        </div>
                      </div>
                    )}
                  </div>

                  {/* View Details Link */}
                  <div className="mt-4 pt-4 border-t border-eucnet-grey-200">
                    <button
                      onClick={() => navigate(`/oes/${oes.id}`)}
                      className="w-full px-4 py-2 bg-eucnet-blue text-white rounded hover:bg-eucnet-navy text-sm font-medium transition-colors"
                    >
                      {t('common.viewDetails')} →
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* OES Edit Sidebar */}
        <OESEditSidebar
          isOpen={sidebarOpen}
          onClose={() => {
            setSidebarOpen(false);
            setSelectedOES(null);
          }}
          oes={selectedOES}
          assessments={assessments}
          defaultAssessmentId={selectedAssessmentId}
          onOESUpdated={fetchOES}
        />
        <BulkImportOESModal
          isOpen={bulkImportOpen}
          onClose={() => setBulkImportOpen(false)}
          assessments={assessments}
          defaultAssessmentId={selectedAssessmentId}
          onImported={fetchOES}
        />
      <ConfirmModal
        open={confirmOpen}
        title={`Delete ${labels.entityName}?`}
        message={oesToDelete ? `You are about to delete "${oesToDelete.name}".` : `You are about to delete this ${labels.entityName}.`}
        confirmText={t('common.delete')}
        cancelText={t('common.cancel')}
        onConfirm={confirmDeleteOES}
        onCancel={() => {
          setConfirmOpen(false);
          setOesToDelete(null);
        }}
      />
      </div>
    </Layout>
  );
}
